/**
 * This file contains utility functions for handling unit conversions and measurements
 * in the Transmission Asset Network System.
 */

import convert from 'convert-units'
import { debugLog } from '../constants/debug'

/**
 * Type definition for valid units that can be used with the convert-units library
 * This helps TypeScript understand what units are valid for conversion
 */
export type ConvertUnit = Parameters<typeof convert>['0'] extends (from: infer U) => any ? U : never;

/**
 * Gets the default unit from a field configuration object
 * @param field - The field configuration object that may contain a baseUnit property
 * @returns The base unit as a string, or undefined if not found
 */
export function getDefaultUnit(field: any | undefined): string | undefined {
    if (!field) {
        return undefined;
    }
    return field.baseUnit;
}

/**
 * Determines the measurement type (e.g., 'length', 'mass', 'temperature') for a given unit
 * @param unit - The unit to check (e.g., 'm' for meters, 'kg' for kilograms)
 * @returns The measurement type as a string, or undefined if not found
 * @example
 * getMeasureType('m') // returns 'length'
 * getMeasureType('kg') // returns 'mass'
 */
export function getMeasureType(unit: string | undefined): string | undefined {
    // If no unit provided, return undefined
    if (!unit) {
        return undefined;
    }

    try {
        // Get all available measurement types (length, mass, temperature, etc.)
        const measures = convert().measures();

        // Check each measurement type
        for (const measure of measures) {
            // Get all possible units for this measurement type
            const possibilities = convert().possibilities(measure);

            // If our unit is in the possibilities, we found its measurement type
            if (possibilities.includes(unit as ConvertUnit)) {
                return measure;
            }
        }
    } catch (error) {
        debugLog('Error getting measure from unit:', error);
    }

    return undefined;
}
